import React from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import { GROUP_BY_FIELDS, GROUP_LABELS, QUICK_VIEWS, moveGroupField } from '../../screens/pedidos/dashboard/groupBy';

const GROUP_BY_OPTIONS = GROUP_BY_FIELDS.map((field) => ({
  value: field,
  label: GROUP_LABELS[field] || field
}));

const PAGE_SIZES = [10, 25, 50, 100];

const FilterBar = ({
  stores = [],
  state,
  statusOptions = [],
  onPatch,
  onClear,
  onSetGroupFields,
  onSetView,
  onQuickView
}) => {
  const showStoreSelector = stores.length > 1;

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.2, sm: 1.6 },
        borderRadius: 3,
        border: '1px solid #d4d4d8',
        background: 'linear-gradient(120deg, #fff9ec 0%, #f2fbf5 50%, #eff6ff 100%)'
      }}
    >
      <Stack spacing={1.2}>
        <Stack direction='row' alignItems='center' justifyContent='space-between' flexWrap='wrap' gap={1}>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: '1rem', sm: '1.1rem' } }}>Filtros avanzados</Typography>
            <Typography variant='caption' sx={{ color: 'text.secondary' }}>
              Combina estado, fecha, búsqueda y agrupaciones jerárquicas.
            </Typography>
          </Box>

          <Stack direction='row' gap={1}>
            <Button variant='outlined' color='inherit' onClick={onClear} startIcon={<ClearRoundedIcon />}>
              Limpiar
            </Button>
          </Stack>
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} gap={1}>
          {showStoreSelector && (
            <FormControl size='small' sx={{ minWidth: 180 }}>
              <InputLabel id='orders-store-label'>Tienda</InputLabel>
              <Select
                labelId='orders-store-label'
                value={state.store_id}
                label='Tienda'
                onChange={(event) => onPatch({ store_id: event.target.value, page: 1 })}
              >
                {stores.map((store) => (
                  <MenuItem key={store._id} value={store._id}>
                    {store.name || store.business_name || store._id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            size='small'
            type='date'
            label='Desde'
            value={state.date_from}
            onChange={(event) => onPatch({ date_from: event.target.value, page: 1 })}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
          />

          <TextField
            size='small'
            type='date'
            label='Hasta'
            value={state.date_to}
            onChange={(event) => onPatch({ date_to: event.target.value, page: 1 })}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
          />

          <FormControl size='small' sx={{ minWidth: 180 }}>
            <InputLabel id='orders-status-label'>Estado</InputLabel>
            <Select
              labelId='orders-status-label'
              value={state.status}
              label='Estado'
              onChange={(event) => onPatch({ status: event.target.value, page: 1 })}
            >
              <MenuItem value=''>Todos</MenuItem>
              {statusOptions.map((status) => (
                <MenuItem key={status} value={status}>{status}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size='small'
            label='Buscar'
            value={state.search}
            onChange={(event) => onPatch({ search: event.target.value, page: 1 })}
            placeholder='Cliente, SKU, descripción, color, talle...'
            sx={{ flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchRoundedIcon fontSize='small' />
                </InputAdornment>
              )
            }}
          />
        </Stack>

        <Stack direction={{ xs: 'column', lg: 'row' }} gap={1} alignItems={{ xs: 'stretch', lg: 'center' }}>
          <Autocomplete
            multiple
            size='small'
            options={GROUP_BY_OPTIONS}
            value={GROUP_BY_OPTIONS.filter((option) => state.group_fields.includes(option.value))}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            getOptionLabel={(option) => option.label}
            onChange={(_, next) => {
              const nextFields = next.map((item) => item.value);
              onSetGroupFields(nextFields);
            }}
            renderInput={(params) => <TextField {...params} label='Group by' placeholder='Selecciona y ordena' />}
            sx={{ minWidth: { xs: '100%', lg: 380 } }}
          />

          <Stack direction='row' alignItems='center' gap={0.5} flexWrap='wrap'>
            {state.group_fields.map((field, idx) => (
              <Chip
                key={field}
                label={GROUP_LABELS[field] || field}
                color='primary'
                variant='outlined'
                onDelete={() => onSetGroupFields(state.group_fields.filter((item) => item !== field))}
                deleteIcon={<ClearRoundedIcon />}
                icon={(
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip title='Mover izquierda'>
                      <span>
                        <IconButton
                          size='small'
                          disabled={idx === 0}
                          onClick={(event) => {
                            event.stopPropagation();
                            onSetGroupFields(moveGroupField(state.group_fields, idx, idx - 1));
                          }}
                        >
                          <ArrowBackRoundedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title='Mover derecha'>
                      <span>
                        <IconButton
                          size='small'
                          disabled={idx === state.group_fields.length - 1}
                          onClick={(event) => {
                            event.stopPropagation();
                            onSetGroupFields(moveGroupField(state.group_fields, idx, idx + 1));
                          }}
                        >
                          <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                )}
                sx={{
                  height: 32,
                  '& .MuiChip-icon': { marginLeft: 0.2 }
                }}
              />
            ))}
          </Stack>
        </Stack>

        <Stack direction={{ xs: 'column', lg: 'row' }} gap={1} alignItems={{ xs: 'stretch', lg: 'center' }} justifyContent='space-between'>
          <Stack direction='row' gap={0.7} flexWrap='wrap'>
            {QUICK_VIEWS.map((view) => (
              <Button
                key={view.id}
                size='small'
                variant='text'
                startIcon={<AutoAwesomeRoundedIcon sx={{ fontSize: 16 }} />}
                onClick={() => onQuickView(view.fields)}
                sx={{ textTransform: 'none' }}
              >
                {view.label}
              </Button>
            ))}
          </Stack>

          <Stack direction='row' gap={1} flexWrap='wrap'>
            <FormControl size='small' sx={{ minWidth: 120 }}>
              <InputLabel id='group-page-size-label'>Pág/grupos</InputLabel>
              <Select
                labelId='group-page-size-label'
                value={state.page_size}
                label='Pág/grupos'
                onChange={(event) => onPatch({ page_size: Number(event.target.value), page: 1 })}
              >
                {PAGE_SIZES.map((size) => (
                  <MenuItem key={size} value={size}>{size}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size='small' sx={{ minWidth: 120 }}>
              <InputLabel id='items-page-size-label'>Pág/items</InputLabel>
              <Select
                labelId='items-page-size-label'
                value={state.item_page_size}
                label='Pág/items'
                onChange={(event) => onPatch({ item_page_size: Number(event.target.value), item_page: 1 })}
              >
                {PAGE_SIZES.map((size) => (
                  <MenuItem key={size} value={size}>{size}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <ToggleButtonGroup
              size='small'
              exclusive
              value={state.view}
              onChange={(_, next) => next && onSetView(next)}
              color='primary'
            >
              <ToggleButton value='summary'>Resumen</ToggleButton>
              <ToggleButton value='table'>Tabla</ToggleButton>
              <ToggleButton value='cards'>Tarjetas</ToggleButton>
              <ToggleButton value='detail'>Detalle</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default FilterBar;
